import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import TaskSubNav from '../components/TaskSubNav';
import { DSBackLink, DSButton, DSTextarea, DSCard, DSIcon, DSBadge } from '../design-system/primitives';
import StatusBadge from '../components/StatusBadge';
import './taskManagementShared.css';

function TaskForceUndo() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [task, setTask] = useState(null);
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState(true);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setCurrentUser(authService.getCurrentUser());
    apiService.getTask(id)
      .then((res) => setTask(res.data))
      .catch((err) => setError(err.response?.data?.error || 'Unable to load task'))
      .finally(() => setLoading(false));
  }, [id]);

  const formatCurrency = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value || 0));

  const handleOverride = async () => {
    try {
      setSubmitting(true);
      await apiService.undoTaskCompletion(id, { reason: note || undefined, notify });
      navigate(`/tasks/${id}`, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to override completion');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading-inline">Loading...</div>;
  }

  const isConfirmed = task?.status === 'confirmed';

  // Post-confirmation (paid) overrides are Admin-only, per the §10.1 lock rule -
  // Task Delegates cannot force-undo once a bounty has been paid out.
  if (isConfirmed && currentUser?.role !== 'admin') {
    return <div className="error-message">Only an admin can override a confirmed (paid) completion.</div>;
  }

  if (!task || !['completed', 'pending_confirmation', 'confirmed'].includes(task.status)) {
    return <div className="error-message">No active completion to override</div>;
  }

  const completionEntry = (task.activity || []).find((a) => a.action === 'completed');

  return (
    <div className="tm-page">
      <DSBackLink onClick={() => navigate(-1)}>Back</DSBackLink>
      <div className="tm-section">
        <h1>Override Completion</h1>
        {error && <div className="error-message">{error}</div>}

        <DSCard variant="elevated" bgVariant="default" borderVariant="subtle" cornerVariant="lg" className="tm-suggestion-card">
          <span className="tm-suggestion-meta">TASK</span>
          <h3>{task.title}</h3>
          <div className="tm-task-tags">
            <DSBadge variant="brand" size="sm" leadingIcon={<DSIcon name="star" />}>{task.point_value} pts</DSBadge>
            {task.bounty_amount && (
              <StatusBadge variant="warning" size="sm" leadingIcon={<DSIcon name="dollar" />}>{formatCurrency(task.bounty_amount)} {isConfirmed ? 'paid' : 'pending'}
              </StatusBadge>
            )}
          </div>
        </DSCard>

        {completionEntry && (
          <DSCard variant="elevated" bgVariant="default" borderVariant="subtle" cornerVariant="lg" className="tm-suggestion-card">
            <span className="tm-suggestion-meta">CURRENTLY CREDITED TO</span>
            <strong>{completionEntry.performed_by_name || 'Unknown'}</strong>
            <div className="tm-suggestion-meta">Completed {new Date(completionEntry.created_at).toLocaleDateString()}</div>
          </DSCard>
        )}

        {isConfirmed && (
          <div className="tm-danger-box">
            <DSIcon name="warning" /> A {formatCurrency(task.bounty_amount)} bounty has already been paid. Overriding will clawback the
            credits and issue a notification (if enabled below). This action is logged permanently in the audit trail.
          </div>
        )}

        <div className="tm-form-group">
          <DSTextarea label="Admin Note (optional)" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Explain why you're overriding this completion..." />
          <div className="tm-suggestion-meta">The member will see this note in their notification.</div>
        </div>

        <div className="tm-form-group">
          <label>
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
            {' '}Notify affected member(s) about this override
          </label>
        </div>

        <div className="tm-actions">
          <DSButton appearance="solid" tone="danger" onClick={handleOverride} disabled={submitting} leadingIcon={<DSIcon name="warning" />}>{isConfirmed ? 'Override & Clawback Credits' : 'Override Completion'}
          </DSButton>
          <DSButton appearance="outline" tone="neutral" onClick={() => navigate(-1)} disabled={submitting}>Cancel</DSButton>
        </div>
      </div>
      <TaskSubNav />
    </div>
  );
}

export default TaskForceUndo;
